import { mapState, mapMutations } from 'vuex';

import { SET_CHANGED_DATA, SET_CHANGED_OPTION } from '@/store/types/mutation-types';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { multiFilter } from '@/utils/array';
import { getNumberFromCssValue, getUnitFromCssValue } from '@/utils/string';
import mapFilters from '@/utils/map-filters';

import {
    generateClass,
    getClassDefinition,
    getCssProperties,
    getTailwindThemeObject,
    tailwindThemePropSort,
} from '@/utils/tailwind-helper';

import {
    FlexClassCollection,
    tailwindConfig,
    TailwindDefinition,
    tailwindDefinitions,
    tailwindSideCharacterMap,
    tailwindUnitCharacterMap,
} from '@/data/tailwind';

export default {
    mixins: [listenOnRootMixin],

    data() {
        return {
            side: '',
            unit: '',
            defaultUnit: '', // overridden in certain options
            isValid: true,
        };
    },

    computed: {
        ...mapState({
            changedOption: state => state.page.changedOption,
            deviceMode: state => state.page.deviceMode,
            selectedElements: state => state.page.selectedElements,
            settings: state => state.settings,
        }),

        displayUnit() {
            return this.unit === 'rem' ? 'px' : this.unit;
        },

        // returns the flex class that most closely matches the current screen, side, and unit
        flexClass() {
            let classes = [...this.flexClasses];

            // first, search for any classes that match the current state
            // if none are found, then we'll leave the classes intact
            const stateFilters = [
                c => getClassDefinition(c).state === this.state,
                () => true,
            ];

            classes = multiFilter(classes, stateFilters);

            // next, search for any classes that match the current screen size
            // if none are found, we'll cast a wider net and search for any that are meant for all screen sizes
            const screenFilters = [
                c => getClassDefinition(c).screen === this.screen,
                c => getClassDefinition(c).screen === 'all' || getClassDefinition(c).screen === 'des',
            ];

            classes = multiFilter(classes, screenFilters);

            // next, search for any classes that match the current side
            // if none are found then we'll cast a wider net and search for classes that are applied to all sides
            // if still none are found then we'll grab classes that matches ANY side
            if (this.side) {
                const sideFilters = [
                    c => getClassDefinition(c).side === this.side,
                    c => !getClassDefinition(c).side,
                    c => !!getClassDefinition(c).side,
                ];

                classes = multiFilter(classes, sideFilters);
            }

            // finally, search for any classes that match the current css unit
            // if none are found then we'll cast a wider net and search for classes that match ANY unit
            if (this.unit) {
                const unitFilters = [
                    c => getClassDefinition(c).unit === this.unit,
                    c => !!getClassDefinition(c).unit,
                ];

                classes = multiFilter(classes, unitFilters);
            }

            return classes[0];
        },

        flexClassDefinition() {
            return getClassDefinition(this.flexClass);
        },

        // returns all of the existing flex classes for the current flex class group
        flexClasses() {
            return Object.values(this.flexClassGroup)
                .map((flexClassSubgroup) => {
                    return Object.values(flexClassSubgroup).filter(c => c);
                })
                .flat();
        },

        flexClassGroup() {
            if (!this.tailwindDefinitionProp) {
                return new FlexClassCollection();
            }

            return this.flexClassCollection[this.tailwindDefinitionProp];
        },

        flexClassCollection() {
            return this.selectedElements[0].flexClassCollection;
        },

        // for setting default option selection
        // set value to first selected element
        miscClasses() {
            return this.selectedElements[0].miscClasses;
        },

        optionName() {
            return this.$vnode.componentOptions.tag;
        },

        options() {
            return this.themeObjectValues.map((value) => {
                return { text: this.titleCase(value), value };
            });
        },

        screen() {
            return this.deviceMode.name;
        },

        tailwindDefinition() {
            return tailwindDefinitions[this.tailwindDefinitionProp] || new TailwindDefinition({});
        },

        tailwindThemeObject() {
            return tailwindConfig.theme[this.tailwindDefinitionProp];
        },

        themeObjectKeys() {
            let keys = Object.keys(this.tailwindThemeObject);

            if (this.excludeKeys) {
                keys = keys.filter(k => !this.excludeKeys.includes(k));
            }

            if (this.unit) {
                let sortFunc = (a, b) => a - b;

                if (this.unit === '%') {
                    sortFunc = (a, b) => {
                        a = a.split('/');
                        b = b.split('/');

                        const aNum = Number(a[0]) / Number(a[1]);
                        const bNum = Number(b[0]) / Number(b[1]);

                        return aNum - bNum;
                    };
                }

                keys = Object.keys(this.tailwindThemeObject)
                    .filter(key => this.tailwindThemeObject[key].includes(this.unit))
                    .sort(sortFunc);

                // strip out any non-unique values
                keys = Array.from(new Set(keys));

                return keys;
            }

            // strip out any non-unique values
            keys = Array.from(new Set(keys));

            // sort the keys in ascending order
            keys = keys.sort(tailwindThemePropSort);

            return keys;
        },

        themeObjectValues() {
            let values = Object.values(this.tailwindThemeObject);

            if (this.unit) {
                values = Object.values(this.tailwindThemeObject).filter(val => val.includes(this.unit));
            }

            if (this.excludeKeys) {
                const excludedValues = this.excludeKeys.map(k => this.tailwindThemeObject[k]);

                values = values.filter(v => !excludedValues.includes(v));
            }

            // sort the values in ascending order
            values = values.sort(tailwindThemePropSort);

            // strip out any non-unique values
            values = Array.from(new Set(values));

            return values;
        },

        sideOptions() {
            const sideOptions = [
                { text: 'All', value: '' },
            ];

            this.tailwindDefinition.sides.forEach((side) => {
                sideOptions.push({
                    text: this.titleCase(tailwindSideCharacterMap[side]),
                    value: side,
                });
            });

            return sideOptions;
        },

        unitOptions() {
            return this.tailwindDefinition.units.map((unit) => {
                return {
                    text: this.titleCase(tailwindUnitCharacterMap[unit]),
                    value: unit,
                };
            });
        },
    },

    watch: {
        deviceMode() {
            this.onSelectedElement();
        },

        selectedElements: {
            handler() {
                this.onSelectedElement();
            },
            immediate: true,
        },
    },

    mounted() {
        this.listenOnRoot('flex::preset::update', this.onSelectedElement);
        this.validateOption();
    },

    methods: {
        ...mapFilters(['paramCase', 'titleCase']),

        ...mapMutations({
            setChangedData: SET_CHANGED_DATA,
            setChangedOption: SET_CHANGED_OPTION,
        }),

        // value = the CSS property value (e.g. 10px)
        // side = the side it's being applied to (e.g. "t" for top, "b" for bottom, etc)
        // state = the state variant (e.g. "hover")
        addFlexClass(classData, elemIndex) {
            const {
                key,
                value,
                side,
                state,
                screen = this.screen,
                tailwindDefinitionProp = this.tailwindDefinitionProp,
            } = classData;

            const className = generateClass({
                key,
                value,
                side,
                state,
                screen,
                tailwindDefinitionProp,
            });

            const classType = side || state || 'default';

            if (typeof elemIndex !== 'undefined') {
                this.selectedElements[elemIndex].currentPreset = '';
                this.selectedElements[elemIndex].flexClassCollection[tailwindDefinitionProp][screen][classType] = className;
            } else {
                this.selectedElements.forEach((element) => {
                    element.currentPreset = '';
                    element.flexClassCollection[tailwindDefinitionProp][screen][classType] = className;
                });
            }

            this.clearPresets();
            this.setChangedData(true);

            return className;
        },

        // you can pass either a single prop as a string, or an array of props
        removeFlexClass(classType, screen = this.screen, tailwindDefinitionProp = this.tailwindDefinitionProp) {
            if (classType) {
                // remove only the specified class(es)
                [].concat(classType).forEach((type) => {
                    this.selectedElements.forEach((element) => {
                        element.flexClassCollection[tailwindDefinitionProp][screen][type] = '';
                    });
                });
            } else {
                // remove ALL classes
                this.selectedElements.forEach((element) => {
                    const tailwindDefinitionPropSection = element.flexClassCollection[tailwindDefinitionProp][screen];
                    const classKeys = Object.keys(tailwindDefinitionPropSection);

                    classKeys.forEach((classKey) => {
                        tailwindDefinitionPropSection[classKey] = '';
                    });
                });
            }

            this.setChangedData(true);
        },

        toggleUtilityClass(className, isAdding) {
            const screenPrefix = this.screen === 'all' ? '' : `${ this.screen }:`;
            const screenClass = screenPrefix + className;
            const classExists = this.hasUtilityClass(screenClass);

            switch (true) {
                case isAdding === true:
                    if (!classExists) this.addUtilityClass(screenClass);

                    break;

                case isAdding === false:
                    if (classExists) this.removeUtilityClass(screenClass);

                    break;

                default:
                    if (!classExists) this.addUtilityClass(screenClass);
                    if (classExists) this.removeUtilityClass(screenClass);
            }

            this.clearPresets();
            this.setChangedData(true);
        },

        addUtilityClass(className) {
            this.selectedElements.forEach(selectedElement => selectedElement.miscClasses.push(className));
        },

        removeUtilityClass(className) {
            this.selectedElements.forEach((selectedElement) => {
                selectedElement.miscClasses.splice(selectedElement.miscClasses.indexOf(className), 1);
            });
        },

        hasUtilityClass(className) {
            return this.selectedElements.some(selectedElement => selectedElement.miscClasses.includes(className));
        },

        clearPresets() {
            this.selectedElements.forEach((elem) => {
                elem.presetId = '';
            });

            this.$root.$emit('flex::preset::remove');
        },

        // updates the store with information about the changed option
        // ... so other options can react accordingly
        async onOptionChange(value) {
            const optionData = {
                option: this.optionName,
                sides: [],
                value, // for options that manipulate classes, this is set to the flex class that was added
            };

            if (this.sliderIndex > 0 && this.side) {
                optionData.sides = [this.side];
            }

            if (this.sliderIndex > 0 && !this.side && this.tailwindDefinition) {
                optionData.sides = this.tailwindDefinition.sides;
            }

            this.setChangedOption(optionData);
            await this.$nextTick();
            this.validateOption();
        },

        // returns the index of the Tailwind value (for the current Tailwind prop)
        // ... that most closely matches the passed-in computed CSS value
        getClosestIndexByValue(computedValue) {
            const closestValue = this.getClosestValue(computedValue);

            return this.getIndexByValue(closestValue);
        },

        // gets the Tailwind value that is closest to the passed in value
        // this helps the slider-based options set a sensible default
        getClosestValue(pxValue) {
            const pxNumberValue = getNumberFromCssValue(pxValue);
            let closestValue = '';
            let smallestDiff = 1000;

            this.themeObjectValues.forEach((tailwindValue) => {
                let tailwindNumberValue = getNumberFromCssValue(tailwindValue);

                if (getUnitFromCssValue(tailwindValue) === 'rem') {
                    tailwindNumberValue *= 16;
                }

                if (this.tailwindDefinitionProp === 'lineHeight') {
                    // special case for line height
                    // which is unit-less, and based on the font size
                    tailwindNumberValue *= getNumberFromCssValue(this.getComputedValue('font-size'));
                }

                const diff = Math.abs(tailwindNumberValue - pxNumberValue);

                if (diff < smallestDiff) {
                    closestValue = tailwindValue;
                    smallestDiff = diff;
                }
            });

            return closestValue;
        },

        // gets the computed style value for a given CSS prop on the currently selected DOM element
        getComputedValue(cssProp, elem = this.selectedElements[0].$el) {
            if (!elem) return '';

            cssProp = cssProp || getCssProperties(this.tailwindDefinitionProp, this.side)[0];

            return window.getComputedStyle(elem).getPropertyValue(cssProp).replace(/"/g, '');
        },

        // gets the (slider) index for the passed-in flex class
        getIndexByClass(className) {
            const { key } = getClassDefinition(className);

            return this.themeObjectKeys.findIndex(k => k === key);
        },

        // gets the (slider) index that most closely reflects the computed CSS property value
        getIndexByComputedProperty() {
            const cssProp = getCssProperties(this.tailwindDefinitionProp, this.side)[0];
            let computedValue = this.getComputedValue(cssProp);

            if (this.side && !computedValue) {
                // if no computed value exists for the current side, then we'll use the "none-side" value
                // e.g. the computed value for "padding" instead of "padding-left"
                computedValue = this.getComputedValue(this.paramCase(this.tailwindDefinitionProp));
            }

            return this.getClosestIndexByValue(computedValue);
        },

        getIndexByValue(value) {
            return this.themeObjectKeys.findIndex(k => this.tailwindThemeObject[k] === value);
        },

        onSelectedElement() {
            // this method is overwritten inside each option
        },

        validateOption() {
            const $element = this.selectedElements[0];

            if ($element?.$attrs?.requiredProperties
                && $element.atomicType === 'atom') {
                this.isValid = $element.isRequiredPropertyValid(this.optionName);
            }
        },

        removeClasses() {
            if (this.side === 'x') {
                this.removeFlexClass(['l', 'r']);
            }

            if (this.side === 'y') {
                this.removeFlexClass(['t', 'b']);
            }

            if (!this.side) {
                this.removeFlexClass(['t', 'r', 'b', 'l', 'x', 'y']);
            }

            if (!this.side && this.sliderIndex === 0) {
                this.removeFlexClass('default');
            }
        },

        setDefaultColor() {
            const defaultColor = getTailwindThemeObject('textColor').black;
            const colorStates = ['default', 'hover', 'selected', 'expanded', 'matching'];

            colorStates.forEach((state) => {
                const colorPropName = state === 'default'
                    ? 'color'
                    : `${ state }Color`;

                let color = getClassDefinition(this.flexClassGroup[this.screen][state]).value;

                // if no responsive class exists, check if a non-responsive class is set, and use that value instead
                if (this.screen !== 'all' && !color) {
                    color = getClassDefinition(this.flexClassGroup.all[state]).value;
                }

                this[colorPropName] = color || defaultColor;
            });
        },

        setSide() {
            this.side = this.flexClassDefinition.side || '';
        },

        setState() {
            this.state = this.flexClassDefinition.state || '';
        },

        setUnit() {
            this.unit = this.flexClassDefinition.unit || this.defaultUnit;
        },

        keySort(a, b) {
            return a - b;
        },

        valueSort(a, b) {
            if (typeof a === 'string') a = a.replace(/(?:[a-zA-Z]|%)/g, '');
            if (typeof b === 'string') b = b.replace(/(?:[a-zA-Z]|%)/g, '');

            return a - b;
        },
    },
};
